'use client';
import { usePathname, useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/auth.store';
import { useDashboardStore } from '@/store/dashboard.store';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import {
  Home, ClipboardList, User,
  Store, Users, Briefcase, Building2, CalendarDays
} from 'lucide-react';

const HIDDEN_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/onboarding'];

type TabItem = {
  label: string;
  icon: React.ReactNode;
  section?: string;
  href?: string;
  badge?: number;
};

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuthStore();
  const { isSpecialist, specialistId, activeRole, setMobileMenuOpen, setActiveSection: setActiveSectionStore } = useDashboardStore();

  const isDashboard = pathname === '/dashboard';

  const { data: pendingData } = useQuery({
    queryKey: ['specialist-pending-count'],
    queryFn: () => api.get('/bookings/specialist/list?status=PENDING').then(r => r.data.data),
    enabled: !!user && isSpecialist,
    refetchInterval: 30000,
  });

  const { data: exchangeData } = useQuery({
    queryKey: ['specialist-exchange-count', specialistId],
    queryFn: () => api.get(`/requests/available/${specialistId}`).then(r => r.data.data),
    enabled: !!user && isSpecialist && !!specialistId,
    refetchInterval: 60000,
  });

  const { data: myRequestsData } = useQuery({
    queryKey: ['client-requests-count'],
    queryFn: () => api.get('/requests/my').then(r => r.data.data),
    enabled: !!user,
    refetchInterval: 60000,
  });

  if (HIDDEN_PATHS.some(p => pathname.startsWith(p))) return null;
  if (!user) return null;

  const pendingCount = Array.isArray(pendingData) ? pendingData.length : 0;
  const exchangeCount = Array.isArray(exchangeData) ? exchangeData.length : 0;
  const clientExchangeCount = Array.isArray(myRequestsData)
    ? myRequestsData.filter((r: any) => r.responsesCount > 0).length
    : 0;

  const navigate = (section?: string, href?: string) => {
    setMobileMenuOpen(false);
    if (href) { router.push(href); return; }
    if (section) {
      // Всегда обновляем секцию напрямую в store (работает даже если URL не меняется)
      setActiveSectionStore(section);
      router.push(`/dashboard?section=${section}`);
      return;
    }
  };

  const isActive = (section?: string, href?: string): boolean => {
    if (href) return pathname === href;
    if (!section || !isDashboard) return false;
    const params = new URLSearchParams(window.location.search);
    const currentSection = params.get('section') ?? '';
    if (section === 'bookings' && !currentSection && activeRole === 'client') return true;
    if (section === 'pro-bookings' && !currentSection && activeRole === 'pro') return true;
    if (section === 'org-bookings' && !currentSection && activeRole === 'org') return true;
    return currentSection === section;
  };

  const clientTabs: TabItem[] = [
    { label: 'Поиск',   icon: <Home size={22} />,          href: '/' },
    { label: 'Записи',  icon: <ClipboardList size={22} />, section: 'bookings' },
    { label: 'Биржа',   icon: <Store size={22} />,         section: 'requests', badge: clientExchangeCount },
    { label: 'Профиль', icon: <User size={22} />,          section: 'mobile-profile' },
  ];

  const proTabs: TabItem[] = [
    { label: 'Заявки',  icon: <Briefcase size={22} />, section: 'pro-bookings', badge: pendingCount },
    { label: 'Биржа',   icon: <Store size={22} />,     section: 'myresponses',  badge: exchangeCount },
    { label: 'Клиенты', icon: <Users size={22} />,     section: 'clients' },
    { label: 'Профиль', icon: <User size={22} />,      section: 'mobile-profile' },
  ];

  const orgTabs: TabItem[] = [
    { label: 'Заявки',  icon: <Briefcase size={22} />,    section: 'org-bookings', badge: pendingCount },
    { label: 'Команда', icon: <Users size={22} />,         section: 'org-members' },
    { label: 'График',  icon: <CalendarDays size={22} />,  section: 'org-schedule' },
    { label: 'Профиль', icon: <Building2 size={22} />,     section: 'org-settings' },
  ];

  const tabs =
    activeRole === 'org' ? orgTabs :
    activeRole === 'pro' ? proTabs :
    clientTabs;

  const accentColor = activeRole === 'org' ? '#185FA5' : 'var(--color-brand)';
  const activeBg    = activeRole === 'org' ? '#EBF3FC' : '#f0faf9';

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-bg-surface border-t border-border"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-stretch h-[68px]">
        {tabs.map((tab) => {
          const active = isActive(tab.section, tab.href);
          return (
            <button
              key={tab.label}
              onClick={() => navigate(tab.section, tab.href)}
              className="relative flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors rounded-none"
              style={{ color: active ? accentColor : 'var(--color-text-placeholder)', backgroundColor: active ? activeBg : 'transparent' }}
            >
              {active && (
                <span
                  className="absolute top-0 left-2 right-2 h-0.5 rounded-b"
                  style={{ backgroundColor: accentColor }}
                />
              )}
              <span className="relative">
                {tab.icon}
                {tab.badge && tab.badge > 0 ? (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full text-white flex items-center justify-center"
                    style={{ fontSize: '10px', backgroundColor: 'var(--color-danger-light)' }}
                  >
                    {tab.badge > 9 ? '9+' : tab.badge}
                  </span>
                ) : null}
              </span>
              <span
                className="text-center leading-none"
                style={{ fontSize: '10px', fontWeight: active ? 600 : 400 }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
